import React from 'react';
import styled from 'styled-components';
import { Icon, Tooltip } from '~/components';
import { colors } from '~/styles';
import { Mono } from '../ai/replay/shared';
import MemberDisplay from '../ai/replay/MemberDisplay';

// Who performed a trace, in one cell: the member (avatar + name) when a member
// drove it — with a spy icon beside the name when a Ruddr admin was impersonating
// that member — the API key when an API key did, or the system/AI/admin otherwise.
// Collapses the separate "member" and "actor" columns into a single performer.

// Every non-member actor renders as plain text matching the table body (the same
// black, same size as the "Started" column) so System, AI Assistant, API Key and
// Ruddr Admin read consistently rather than each styled differently.
const ActorText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${colors.black};
`;

// The impersonation marker sits inline after the member's name, small and grey.
// Uses the project Tooltip (shows immediately — no delay) rather than a native
// title, which is slow to appear.
const SpyBadge = styled(Tooltip).attrs({ Container: 'span', placement: 'top' })`
  display: inline-flex;
  align-items: center;
  margin-left: 0.375rem;
  color: ${colors.grey40};
  font-size: 0.6875rem;
`;

export default function ActorCell({ actor, member, apiKeyId, admin, size }) {
  if (actor === 'member') {
    // An impersonating admin marks the member action with a spy icon beside the
    // name; the admin's name (resolved by the replay reader) shows on hover.
    const badge = admin ? (
      <SpyBadge message={`Impersonated by ${admin.name ?? admin.email ?? admin.id}`}>
        <Icon icon="user-secret" />
      </SpyBadge>
    ) : null;
    return <MemberDisplay member={member} size={size} badge={badge} />;
  }
  if (apiKeyId) {
    return (
      <ActorText>
        <Icon icon="key" color={colors.grey40} />
        API Key
        <Mono>{apiKeyId.slice(0, 8)}</Mono>
      </ActorText>
    );
  }
  if (actor === 'agent') return <ActorText>AI Assistant</ActorText>;
  if (actor === 'admin') return <ActorText>Ruddr Admin</ActorText>;
  if (!actor || actor === 'system') return <ActorText>System</ActorText>;
  return <ActorText>{actor}</ActorText>;
}
